import React, { FC, useMemo } from 'react';

import { IssuesCount } from 'src/apps/dashboard/services/queries/dashboard/helpers/transformResults';

import { AccessibilitySummaryCard } from '../../AccessibilitySummaryCard';
import { INFO_CONTENT } from '../../constants';
import { transformAccessibilityData } from '../../helpers/transformAccessibilityData';
import {
	getNAStatusTag,
	getStatusTag,
	getTrendAriaLabel,
	getTrendTag
} from '../../helpers/uiHelpers';

import './AccessibilitySummary.scss';

interface AccessibilitySummaryProps {
	data: IssuesCount[];
	selectedPeriod?: number;
	className?: string;
}

export const AccessibilitySummary: FC<AccessibilitySummaryProps> = ({
	data: rawData,
	selectedPeriod,
	className = ''
}) => {
	const data = useMemo(
		() => transformAccessibilityData(rawData, selectedPeriod),
		[rawData, selectedPeriod]
	);

	return (
		<div
			className={`accessibility-summary ${className}`}
			aria-label="Accessibility validation results summary"
		>
			<AccessibilitySummaryCard
				type="score"
				id="accessibility-score"
				title="Accessibility score"
				className="accessibility-score-card"
				infoContent={INFO_CONTENT.ACCESSIBILITY_SCORE}
				ariaLabel={
					data.groupedScore.score === null
						? 'Score: No data available'
						: `Score: ${data.groupedScore.score} out of 100`
				}
				score={data.groupedScore.score}
				maxScore={100}
				statusTag={
					data.groupedScore.score === null
						? getNAStatusTag()
						: getStatusTag(data.groupedScore.status)
				}
			/>

			{/* Divider after Accessibility Score */}
			<div className="metric-divider" aria-hidden="true" />

			{/* Unique Issues Group */}
			<div className="metric-group">
				<AccessibilitySummaryCard
					type="metric"
					id="unique-issues"
					title="Unique issues"
					className="unique-issues-card"
					infoContent={INFO_CONTENT.UNIQUE_ISSUES}
					ariaLabel={`Unique issues: ${data.uniqueIssues.allIssues.value}`}
					value={data.uniqueIssues.allIssues.value}
					trendTag={getTrendTag(data.uniqueIssues.allIssues)}
					trendAriaLabel={getTrendAriaLabel(data.uniqueIssues.allIssues)}
				/>

				<AccessibilitySummaryCard
					type="metric"
					id="unique-critical-issues"
					title="Unique critical issues"
					className="unique-critical-issues-card"
					infoContent={INFO_CONTENT.UNIQUE_CRITICAL_ISSUES}
					ariaLabel={`Unique critical issues: ${data.uniqueIssues.critical.value}`}
					value={data.uniqueIssues.critical.value}
					trendTag={getTrendTag(data.uniqueIssues.critical)}
					trendAriaLabel={getTrendAriaLabel(data.uniqueIssues.critical)}
				/>
			</div>

			{data.totalIssues && (
				<>
					{/* Divider after Unique Critical Issues */}
					<div className="metric-divider" aria-hidden="true" />

					{/* Total Issues Group */}
					<div className="metric-group">
						<AccessibilitySummaryCard
							type="metric"
							id="total-issues"
							title="Total issues"
							className="total-issues-card"
							infoContent={INFO_CONTENT.TOTAL_ISSUES}
							ariaLabel={`Total issues: ${data.totalIssues.allIssues.value}`}
							value={data.totalIssues.allIssues.value}
							trendTag={getTrendTag(data.totalIssues.allIssues)}
							trendAriaLabel={getTrendAriaLabel(data.totalIssues.allIssues)}
						/>

						<AccessibilitySummaryCard
							type="metric"
							id="total-critical-issues"
							title="Total critical issues"
							className="total-critical-issues-card"
							infoContent={INFO_CONTENT.TOTAL_CRITICAL_ISSUES}
							ariaLabel={`Total critical issues: ${data.totalIssues.critical.value}`}
							value={data.totalIssues.critical.value}
							trendTag={getTrendTag(data.totalIssues.critical)}
							trendAriaLabel={getTrendAriaLabel(data.totalIssues.critical)}
						/>
					</div>
				</>
			)}
		</div>
	);
};
